import React, { FC, SVGProps, SyntheticEvent, useCallback, useMemo } from "react";

import Modal, { ModalBody, ModalHeader, Sheets } from "@ingka/modal";
import clipboardCheckmark from "@ingka/ssr-icon/paths/clipboard-checkmark";
import display from "@ingka/ssr-icon/paths/display";
import globe from "@ingka/ssr-icon/paths/globe";
import people from "@ingka/ssr-icon/paths/people";
import dataChart from "@ingka/ssr-icon/paths/data-chart";
import ListView, { ListViewItem } from "@ingka/list-view";
import { ListBoxGroup } from "@ingka/list-box";

import useTabs from "../../../hooks/useTabs";
import { HomePageLayoutTabs } from "../../../utils/constants";
import useStore, { activeApplicationSelector } from "../../../store";

type SSRIconPath = ((prefix?: string | undefined) => SVGProps<SVGElement>[]) | undefined;

type TabValue =
	| HomePageLayoutTabs.Configuration
	| HomePageLayoutTabs.Schemas
	| HomePageLayoutTabs.Markets
	| HomePageLayoutTabs.MarketDashboard
	| HomePageLayoutTabs.Dashboard
	| HomePageLayoutTabs.Users
	| HomePageLayoutTabs.UsersRequested;

interface NavigationItem {
	id: string;
	label: string;
	tab: TabValue;
	icon: SSRIconPath;
	visible: boolean;
	menuItems?: {
		id: string;
		label: string;
		tab: TabValue;
		disabled?: boolean;
	}[];
}

interface MenuLayoutProps {
	showModal: boolean;
	showAdminLayout: boolean;
	setShowModal: (value: boolean) => void;
}

const Layout: FC<MenuLayoutProps> = ({ showModal, setShowModal, showAdminLayout }) => {
	const { setActiveTab, getTabLink } = useTabs();
	const activeApplication = useStore(activeApplicationSelector);

	const navItems = useMemo<NavigationItem[]>(
		() => [
			{
				id: "configuration",
				label: "Configuration",
				tab: HomePageLayoutTabs.Configuration,
				icon: clipboardCheckmark,
				visible: true,
			},
			{
				id: "schemas",
				label: "Schemas",
				tab: HomePageLayoutTabs.Schemas,
				icon: display,
				visible: Boolean(showAdminLayout),
			},
			{
				id: "markets",
				label: "Markets",
				tab: HomePageLayoutTabs.Markets,
				icon: globe,
				visible: Boolean(showAdminLayout),
			},
			{
				id: "dashboard",
				label: "Dashboard",
				tab: HomePageLayoutTabs.Dashboard,
				icon: dataChart,
				visible: Boolean(activeApplication?.showDashboard || activeApplication?.showMarketDashboard),

				menuItems: [
					{
						id: "marketDashboard",
						label: "Market dashboard",
						tab: HomePageLayoutTabs.MarketDashboard,
						disabled: !activeApplication?.showMarketDashboard,
					},
					{
						id: "globalDashboard",
						label: "Global dashboard",
						tab: HomePageLayoutTabs.Dashboard,
						disabled: !activeApplication?.showDashboard,
					},
				],
			},
			{
				id: "users",
				label: "Users",
				tab: HomePageLayoutTabs.Users,
				icon: people,
				visible: Boolean(showAdminLayout),
				menuItems: [
					{ id: "userOnboard", label: "Users onboarded", tab: HomePageLayoutTabs.Users },
					{
						id: "userRequest",
						label: "Users requests",
						tab: HomePageLayoutTabs.UsersRequested,
					},
				],
			},
		],
		[showAdminLayout, activeApplication]
	);

	const handleNavigate = useCallback(
		(e: SyntheticEvent, tab: TabValue) => {
			e.preventDefault();
			setActiveTab(tab);
			setShowModal(false);
		},
		[setActiveTab, setShowModal]
	);

	return (
		<Modal visible={showModal} handleCloseBtn={() => setShowModal(false)}>
			<Sheets
				fullSize
				alignment="right"
				footer={null}
				aria-labelledby="menuModalTitleId"
				header={<ModalHeader title="Menu" titleId="menuModalTitleId" />}
			>
				<ModalBody>
					<nav aria-label="navigation">
						<ListView id="menu-items-list" size="small">
							{navItems
								.filter((item) => item.visible)
								.map(
									(item, index) =>
										(item.menuItems?.length ?? 0) === 0 && (
											<ListViewItem
												data-autofocus={index === 0 ? true : false}
												id={`menu-${item.id}`}
												key={`menu-${item.id}`}
												onClick={(e: React.SyntheticEvent) => handleNavigate(e, item.tab)}
												title={item.label}
												control="link"
												href={getTabLink(item.tab)}
												inset
											/>
										)
								)}
						</ListView>

						{navItems
							.filter((item) => item.visible)
							.map(
								(item) =>
									(item.menuItems?.length ?? 0) > 0 && (
										<ListBoxGroup groupName={item.label} id={`group-menu-${item.id}`}>
											<ListView id="test" size="small">
												{item.menuItems?.map((subItem) => (
													<ListViewItem
														inset
														title={subItem.label}
														control="link"
														id={subItem.id}
														key={subItem.id}
														href={getTabLink(subItem.tab)}
														onClick={(e: React.SyntheticEvent) => handleNavigate(e, subItem.tab)}
													/>
												))}
											</ListView>
										</ListBoxGroup>
									)
							)}
					</nav>
				</ModalBody>
			</Sheets>
		</Modal>
	);
};

export default Layout;
